import getValueOffPercent from 'lib/getValueOffPercent'
import { CreateTripItemRequest } from 'tripPlanner/api/tripItem/types'
import {
  BookmarkDatesAndOccupancy,
  SavedHotelData,
} from 'tripPlanner/types/tripItem'
import { convertBedbankPropertyAddressToRegion } from 'tripPlanner/utils'
import {
  getBedbankCheckInOutTime,
  getCheckInCheckOutOccupancy,
} from 'tripPlanner/utils/itemConversions/common'

type CreateHotelRequestBody = Extract<CreateTripItemRequest, { type: 'HOTEL' }>

interface Params {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  datesAndGuests: BookmarkDatesAndOccupancy
  currentRegionCode: string
  currencyCode: string
  price?: number
  value?: number
  taxExclusivePricing: boolean
  roomTypeId?: string
  templateId?: string
  templateItemId?: string
}

export function createBedbankItemPayload({
  offer,
  datesAndGuests,
  currentRegionCode,
  currencyCode,
  price,
  value,
  taxExclusivePricing,
  roomTypeId,
  templateId,
  templateItemId,
}: Params): CreateHotelRequestBody {
  let region = ''
  if (offer.property && offer.property.address) {
    region = convertBedbankPropertyAddressToRegion(offer.property.address)
  }

  const discountPercent =
    typeof price === 'number' && typeof value === 'number'
      ? getValueOffPercent(value, price)
      : undefined

  // When we do not have any room option for selected dates/guests,
  // we won't get a price.
  // In such cases, we do not want to save dates/guests in the trip item
  const datesAndOccupancy =
    typeof price === 'number' ? getCheckInCheckOutOccupancy(datesAndGuests) : {}

  const savedHotelData: SavedHotelData = {
    offerType: offer.type,
    regionCode: currentRegionCode,
    currencyCode,
    offerId: offer.id,
    packageId: roomTypeId,
    roomTypeId,
    price,
    value,
    discountPercent,
    priceDisplayType: taxExclusivePricing
      ? 'taxesAndFeesExclusive'
      : 'taxesAndFeesInclusive',
    roomTypeName:
      roomTypeId && 'packages' in offer
        ? offer.packages.find((p) => p.roomId === roomTypeId)?.name
        : undefined,
    ...datesAndOccupancy,
  }

  const [checkInTime, checkOutTime] = getBedbankCheckInOutTime(
    offer.property.finePrint,
  )

  const payload: CreateHotelRequestBody = {
    type: 'HOTEL',
    isBooked: false,
    status: 'PLANNED',
    sourceType: 'LE_Hotel_Bedbank',
    place: {
      type: 'GEO_MULTI_DATES',
      id: offer.property.id,
      address: offer.property.address.line1,
      name: offer.name,
      point: {
        lat: offer.property.latitude,
        lng: offer.property.longitude,
      },
      startDate: datesAndOccupancy.checkIn,
      endDate: datesAndOccupancy.checkOut,
      startTime: checkInTime,
      endTime: checkOutTime,
      region,
    },
    code: offer.id,
    subCode: roomTypeId,
    savedItemData: savedHotelData,
    templateId,
    templateItemId,
  }

  return payload
}
